import { rest } from '@0xtorch/core'
import { z } from 'zod'
import { fetchDataWithAutoRetry } from '../../utils/fetchDataWithAutoRetry'
import { bitgetApiEndpoint } from '../constants'
import { createPrivateGetHeaders } from '../utils'
import { type SpotTaxData, spotTaxDataSchema } from './schema'

const responseSchema = z.object({
  code: z.string(),
  msg: z.string(),
  requestTime: z.number().int(),
  data: z.array(spotTaxDataSchema),
})

type FetchSpotTaxHistoriesParameters = {
  apiKey: string
  apiSecret: string
  password: string
  from: number
  to: number
  limit: number
  proxy?: string
  headers?: Record<string, string>
}

const urlPath = '/api/v2/tax/spot-record'

export const fetchSpotTaxHistories = ({
  apiKey,
  apiSecret,
  password,
  from,
  to,
  limit,
  proxy,
  headers,
}: FetchSpotTaxHistoriesParameters): Promise<readonly SpotTaxData[]> =>
  fetchDataWithAutoRetry({
    from,
    to,
    limit,
    desc: false,
    fetchData: async ({ from, to, limit, desc }) => {
      if (desc) {
        throw new Error('desc must be false')
      }
      if (limit > 500) {
        throw new Error('limit must be less than or equal to 100')
      }

      const url = new URL(urlPath, bitgetApiEndpoint)
      url.searchParams.append('limit', limit.toString())
      url.searchParams.append('startTime', from.toString())
      url.searchParams.append('endTime', to.toString())
      const query = url.searchParams.toString()

      const response = await rest(
        proxy === undefined
          ? url.toString()
          : `${proxy}${encodeURIComponent(url.toString())}`,
        {
          fetchOptions: {
            method: 'GET',
            headers: {
              ...headers,
              ...(await createPrivateGetHeaders({
                apiKey,
                apiSecret,
                password,
                urlPath: url.pathname,
                query: query.length > 0 ? `?${query}` : '',
              })),
            },
          },
          schema: responseSchema,
        },
      )

      return response.data
    },
  })
